/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, { AxiosError } from 'axios';
import { useMemo, useState } from 'react';

import { AUTH_REFRESH_TOKEN_URL } from '@/constants/constants';
import { AppError } from '@/utils/AppError';
import { useAuthStore } from '@/stores/AuthStore';

enum ApiStatus {
  OK = 200,
  BAD_REQUEST = 400,
  UNAUTHORIZED = 401,
  NOT_FOUND = 404,
  INTERNAL_SERVER_ERROR = 500,
}

type PromiseType = {
  onSuccess: (token: string) => void;
  onFailure: (error: AxiosError) => void;
};

const VITE_API_URL = import.meta.env.VITE_API_URL;

export function useRestClient() {
  const [failedQueued, setFailedQueued] = useState<Array<PromiseType>>([]);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);

  function netLog(...args: any) {
    if (
      import.meta.env.MODE !== 'production' &&
      import.meta.env.MODE !== 'staging'
    ) {
      console.log(...args);
    }
  }

  //To avoid infinity loop
  const internalClient = axios.create({
    baseURL: VITE_API_URL,
  });

  const client = axios.create({
    baseURL: VITE_API_URL,
  });

  client.interceptors.request.use(
    async function (config) {
      const { getToken } = useAuthStore.getState();
      const tokens = getToken();

      if (tokens?.id_token) {
        config.headers.Authorization = `Bearer ${tokens?.id_token}`;
      }
      netLog('Request URL:', `${config.baseURL}${config.url}`);
      netLog('Request params:', config.params);
      netLog('Request data:', config.data);
      return config;
    },
    function (error) {
      return Promise.reject(error);
    }
  );

  client.interceptors.response.use(
    function (response) {
      // Any status code that lie within the range of 2xx cause this function to trigger

      return response;
    },
    async function (error) {
      // Any status codes that falls outside the range of 2xx cause this function to trigger
      netLog('Request failed with error:', error.message);

      if (error.response?.status === ApiStatus.UNAUTHORIZED) {
        netLog('Unauthorized error received:', error.response.data);

        netLog('Token expired or invalid. Trying to refresh token...');

        const { tokens, clearTokens, updateTokens } = useAuthStore.getState();
        if (!tokens?.refresh_token) {
          clearTokens(); // Limpa os tokens do Zustand e do localStorage
          return Promise.reject(error);
        }

        const { refresh_token, access_token } = tokens;

        const originalRequestConfig = error.config;

        if (isRefreshing) {
          netLog('Already refreshing token. Queuing the request...');
          queueRequest(originalRequestConfig);
        }

        setIsRefreshing(true);

        // eslint-disable-next-line no-async-promise-executor
        return new Promise(async (resolve, reject) => {
          try {
            const data = await getNewTokens(refresh_token, access_token);

            const dataTokens = {
              id_token: data.id_token,
              access_token: data.access_token,
              refresh_token: data.refresh_token,
              expiresAt: data.expiresAt,
              expiresIn: data.expiresIn,
            };

            netLog('Saving new tokens...');
            updateTokens(dataTokens);
            setIsRefreshing(false);

            failedQueued.forEach((request) => {
              request.onSuccess(data.id_token);
            });

            setFailedQueued([]);
            originalRequestConfig.headers.Authorization = `Bearer ${data.id_token}`;
            const newResponse = await internalClient(originalRequestConfig);
            return resolve(newResponse);
          } catch (error: any) {
            failedQueued.forEach((request) => {
              request.onFailure(error);
            });

            clearTokens();
            setIsRefreshing(false);
            setFailedQueued([]);
            return reject(error);
          }
        });
      }

      return Promise.reject(error);
    }
  );

  async function getNewTokens(refresh_token: string, access_token: string) {
    const { data } = await internalClient.post(
      AUTH_REFRESH_TOKEN_URL,
      {
        refreshToken: refresh_token,
      },
      {
        headers: {
          Authorization: `Bearer ${access_token}`,
        },
      }
    );

    return data;
  }

  function queueRequest(originalRequestConfig: any) {
    return new Promise((resolve, reject) => {
      setFailedQueued((prev) => [
        ...prev,
        {
          onSuccess: async (token: string) => {
            try {
              originalRequestConfig.headers.Authorization = `Bearer ${token}`;
              const newResponse = await internalClient(originalRequestConfig);
              resolve(newResponse);
            } catch (error) {
              reject(error);
            }
          },
          onFailure: (error: AxiosError) => {
            reject(error);
          },
        },
      ]);
    });
  }

  return useMemo(
    () => ({
      restClient: client,
    }),
    []
  );
}

export function handleRequestError(error: unknown) {
  try {
    if (error instanceof AxiosError && error.message == 'Network Error') {
      return {
        message: 'common_connectitivy_error',
      } as AppError;
    } else if (
      error instanceof AxiosError &&
      error.response?.status === ApiStatus.UNAUTHORIZED
    ) {
      return {
        message: error.response.data.message,
      } as AppError;
    } else if (
      error instanceof AxiosError &&
      error.response?.status === ApiStatus.BAD_REQUEST
    ) {
      return {
        message: error.response.data.message,
      } as AppError;
    } else if (error instanceof AxiosError && error.response?.data) {
      return error.response?.data as AppError;
    } else if (error instanceof AxiosError && error.code == 'ECONNABORTED') {
      return {
        message: 'service_timeout_error',
      } as AppError;
    } else {
      return {
        message: 'common_service_error',
      } as AppError;
    }
  } catch (e) {
    return {
      message: 'common_service_error',
    } as AppError;
  }
}
